/**
 * App — top-level shell.
 *
 * Boot sequence:
 *   1. Mount QueryClientProvider with the singleton client.
 *   2. Mount Sonner Toaster so any fetch error after first paint can toast.
 *   3. Fire silentRefresh() ONCE outside React (per use-auth.ts comments).
 *      Until it resolves we render a centered spinner; this avoids a
 *      /login flash for users with a valid refresh cookie. On failure we
 *      simply unblock — RequireAuth then redirects to /login.
 *   4. Once silent-refresh has settled, mount the RouterProvider.
 *
 * The boot promise is resolved with a flag (not the access token itself)
 * because the access token is stored in the fetcher module, not React.
 */
import * as React from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';

import { AppErrorBoundary } from '@/components/app-error-boundary';
import { Toaster } from '@/components/ui/toaster';
import { Skeleton } from '@/components/ui/skeleton';
import { silentRefresh } from '@/hooks/use-auth';
import { queryClient } from '@/lib/query-client';
import { createRouter } from '@/routes';

/**
 * Lazy router accessor. We create the router on first render rather than at
 * module-load time so unit tests can fully unmount the app + recreate it
 * across describes without a stale router holding pending navigations.
 */
let routerSingleton: ReturnType<typeof createRouter> | null = null;
function getRouter(): ReturnType<typeof createRouter> {
  if (!routerSingleton) routerSingleton = createRouter();
  return routerSingleton;
}

function BootSplash(): React.ReactElement {
  return (
    <div data-testid="boot-splash" className="grid min-h-screen place-items-center bg-bg p-8">
      <div className="w-full max-w-sm space-y-3">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-9 w-2/3" />
      </div>
    </div>
  );
}

export function App(): React.ReactElement {
  const [booted, setBooted] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    silentRefresh().finally(() => {
      if (!cancelled) setBooted(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // P6 W4 T4.4 — top-level AppErrorBoundary wraps the routed tree.
  //
  // Placement notes:
  //   - Wraps INSIDE `QueryClientProvider` so a render error inside a
  //     query-driven component can still surface the fallback (the
  //     fallback itself doesn't fetch — but a child that DOES throw
  //     needs the QC context if it tries to render again post-recovery).
  //   - Wraps OUTSIDE the router so a fatal error in the router's own
  //     render path lands here. Route-level (`RouteErrorBoundary`)
  //     handles ChunkLoadError specifically; this catches everything
  //     else.
  //   - `<Toaster/>` sits OUTSIDE the boundary so any post-error
  //     re-mount path can still toast (and so a Toaster crash itself
  //     doesn't take the boundary down with it).
  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        {booted ? <RouterProvider router={getRouter()} /> : <BootSplash />}
      </AppErrorBoundary>
      <Toaster />
    </QueryClientProvider>
  );
}
